/**
 * Passkeys in the browser (MFA_PLAN.md): the API hands over WebAuthn options
 * as JSON; these turn them into what navigator.credentials wants and turn
 * the answer back into JSON for the API. base64url both ways.
 */
const toBytes = (b64url) => {
  const s = String(b64url).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(s + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};
const toB64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export function passkeysSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

/** Make a passkey from the API's creation options; returns the attestation as JSON text. */
export async function createPasskey(optionsJson) {
  const o = JSON.parse(optionsJson);
  const publicKey = {
    ...o,
    challenge: toBytes(o.challenge),
    user: {...o.user, id: toBytes(o.user.id)},
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({...c, id: toBytes(c.id)})),
  };
  const cred = await navigator.credentials.create({publicKey});
  return JSON.stringify({
    id: cred.id,
    rawId: toB64Url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: toB64Url(cred.response.attestationObject),
      clientDataJSON: toB64Url(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  });
}

/** Use a passkey against the API's request options; returns the assertion as JSON text. */
export async function getPasskey(optionsJson) {
  const o = JSON.parse(optionsJson);
  const publicKey = {
    ...o,
    challenge: toBytes(o.challenge),
    allowCredentials: (o.allowCredentials || []).map((c) => ({...c, id: toBytes(c.id)})),
  };
  const cred = await navigator.credentials.get({publicKey});
  return JSON.stringify({
    id: cred.id,
    rawId: toB64Url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: toB64Url(cred.response.authenticatorData),
      clientDataJSON: toB64Url(cred.response.clientDataJSON),
      signature: toB64Url(cred.response.signature),
      userHandle: cred.response.userHandle ? toB64Url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  });
}

/**
 * A sentence for the browser errors worth explaining, or null to let the
 * caller use its own. InvalidStateError on create = this device already holds
 * a passkey for the account (the browser's own wording is unreadable).
 */
export function passkeyProblem(e) {
  if (e?.name === 'InvalidStateError') {
    return 'This device already has a passkey for your account. Use that one to sign in, or add a passkey on a different device.';
  }
  if (e?.name === 'SecurityError') return 'Passkeys cannot be used on this address.';
  if (e?.name === 'NotSupportedError') return 'This device cannot make the kind of passkey we need.';
  return null;
}

/** The person closed the browser's sheet: not an error worth showing as one. */
export function passkeyCancelled(e) {
  return e?.name === 'NotAllowedError' || e?.name === 'AbortError';
}
